import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { authApi, ApiError } from '../lib/api';
import { useTheme } from '../context/ThemeContext';

export default function InviteAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');

  const { data: invitation, isLoading, error: fetchError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: () => authApi.validateInvitation(token!),
    enabled: !!token,
  });

  const acceptMutation = useMutation({
    mutationFn: () => authApi.acceptInvitation(token!, name, password),
    onSuccess: () => {
      navigate('/login');
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('アカウント作成に失敗しました');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('パスワードが一致しません');
      return;
    }

    if (password.length < 8) {
      setError('パスワードは8文字以上で入力してください');
      return;
    }

    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*]/.test(password);

    if (!hasUpper || !hasLower || !hasNumber || !hasSpecial) {
      setError('パスワードは大文字・小文字・数字・記号を含めてください');
      return;
    }

    acceptMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-main transition-colors duration-300">
        <div className="text-theme-secondary">読み込み中...</div>
      </div>
    );
  }

  if (fetchError || !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-main px-4 transition-colors duration-300">
        <div className="w-full max-w-md bg-theme-card rounded-xl p-8 border border-theme text-center transition-colors duration-300">
          <div className="text-4xl mb-4">❌</div>
          <h2 className="text-xl font-bold text-theme-primary mb-4">無効な招待リンク</h2>
          <p className="text-theme-secondary mb-6">
            この招待リンクは無効か、有効期限が切れています。
          </p>
          <Link to="/login" className="text-accent hover:underline">
            ログインページへ
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-main px-4 transition-colors duration-300">
      {/* テーマ切り替えボタン */}
      <button
        onClick={toggleTheme}
        className="fixed top-4 right-4 p-3 bg-theme-card border border-theme rounded-lg hover:bg-theme-hover transition-colors"
        title={theme === 'dark' ? 'ライトモード' : 'ダークモード'}
      >
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🤖</div>
          <h1 className="text-3xl font-bold text-accent">Jarvis</h1>
          <p className="text-theme-secondary mt-2">招待を受け入れる</p>
        </div>

        <div className="bg-theme-card rounded-xl p-8 border border-theme shadow-lg transition-colors duration-300">
          <div className="text-center mb-6">
            <p className="text-theme-primary">
              <span className="text-accent font-medium">{invitation.email}</span>
              <br />宛てに招待が送信されました
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500 bg-opacity-20 border border-red-500 rounded-lg text-red-500 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-2">
                お名前
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
                placeholder="山田太郎"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-2">
                パスワード
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
              />
              <p className="text-xs text-theme-secondary mt-1">
                8文字以上、大文字・小文字・数字・記号を含む
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-theme-secondary mb-2">
                パスワード（確認）
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-theme-input border border-theme rounded-lg text-theme-primary placeholder-theme-muted focus:outline-none focus:ring-2 focus:ring-accent transition-colors"
              />
            </div>

            <button
              type="submit"
              disabled={acceptMutation.isPending}
              className="w-full py-3 bg-accent text-navy-900 font-bold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {acceptMutation.isPending ? '作成中...' : 'アカウントを作成'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
